import React from 'react'
import { Redirect } from 'react-router-dom'

// Icons
import ActionDelete from 'material-ui-legacy/svg-icons/action/delete'
import EditorModeEdit from 'material-ui-legacy/svg-icons/editor/mode-edit'
import ContentArchive from 'material-ui-legacy/svg-icons/content/archive'

// Custom UI components
import errors from '../../lib/errors.js'
import IconButton from '../UI/IconButton'
import LinkButton from '../UI/LinkButton'
import InfoLinkButton from '../UI/InfoLinkButton'
import ErrorDialog from '../UI/ErrorDialog'
import ConfirmationDialog from '../UI/ConfirmationDialog'
import LinearLoader from '../UI/LinearLoader'

import ListProjects from './ListProjects'
import Resource from '../../stores/resource'
const ProjectStore = Resource('projects')

// INTERNAL COMPONENTS ---------------------------------------------------------

/*
Component used to display project action buttons within details row on DataGrid
Props:
- projectId: the project id
- archiveCallback,
  markDeadCallback: action callbacks
*/
const ProjectActionButtons = ({
  projectId,
  archiveCallback,
  markDeadCallback
}) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ marginRight: '10px' }}>Actions:</span>
    <InfoLinkButton url={`/projects/${projectId}`} />
    <LinkButton
      url={`/projects/${projectId}/edit`}
      icon={<EditorModeEdit />}
      label="Edit"
    />
    {archiveCallback && (
      <IconButton
        onTouchTap={archiveCallback}
        icon={<ContentArchive />}
        label="Archive"
      />
    )}
    {markDeadCallback && (
      <IconButton
        onTouchTap={markDeadCallback}
        icon={<ActionDelete />}
        label="Mark dead"
      />
    )}
  </div>
)

/*
Component used to display details row on DataGrid
Props:
- row: a quote row from ListProjects (NOT a project object!)
- archiveCallback,
  markDeadCallback: passed down to ProjectActionButtons
*/
const ProjectDetailsRow = ({ row, ...restProps }) => (
  <section style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
    <ProjectActionButtons projectId={row.id} {...restProps} />
  </section>
)

/*
Props:
- archiveCallback: function(), called when archive is confirmed
- cancelCallback: function(), called on cancel
- open: Boolean - show dialog if true
- project: the project object to be deleted
*/
const ProjectArchiveConfirmation = ({
  archiveCallback,
  cancelCallback,
  open,
  project
}) => {
  return (
    <ConfirmationDialog
      confirmLabel="Archive"
      confirmIcon={<ContentArchive />}
      confirmCallback={archiveCallback}
      cancelCallback={cancelCallback}
      title="Archive confirmation"
      open={open}
    >
      <p>
        Are you sure you want to archive the project{' '}
        <strong>{project.attributes.name}</strong>? There's no undo!
      </p>
    </ConfirmationDialog>
  )
}

/*
Props:
- markDeadCallback: function(), called when mark dead is confirmed
- cancelCallback: function(), called on cancel
- open: Boolean - show dialog if true
- project: the project object to be deleted
*/
const ProjectMarkDeadConfirmation = ({
  markDeadCallback,
  cancelCallback,
  open,
  project
}) => {
  return (
    <ConfirmationDialog
      confirmLabel="Mark dead"
      confirmIcon={<ActionDelete />}
      confirmCallback={markDeadCallback}
      cancelCallback={cancelCallback}
      title="Confirm marking project as dead"
      open={open}
    >
      <p>
        Are you sure you want to mark the project{' '}
        <strong>{project.attributes.name}</strong> as dead? There's no undo!
      </p>
    </ConfirmationDialog>
  )
}

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- projectId: Automatically selects a given project (optional)
*/
class ProjectsManager extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      project: null,
      showArchiveConfirmation: false,
      showMarkDeadConfirmation: false,
      error: null,
      refresh: false,
      redirect: ''
    }
  }

  _refresh = () => {
    // Ugly hack! Find a better way to do this!
    this.setState({ refresh: true }, () =>
      setTimeout(this.setState({ refresh: false }), 250)
    )
  }

  _confirmArchive = row => {
    ProjectStore.find(row.id)
      .then(result => {
        this.setState({
          project: result.data,
          showArchiveConfirmation: true
        })
      })
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _cancelArchive = () => this.setState({ showArchiveConfirmation: false })

  _confirmMarkDead = row => {
    ProjectStore.find(row.id)
      .then(result => {
        this.setState({
          project: result.data,
          showMarkDeadConfirmation: true
        })
      })
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _cancelMarkDead = () => this.setState({ showMarkDeadConfirmation: false })

  _updateProjectState = newState => {
    const { project } = this.state

    ProjectStore.update(project.id, { state: newState })
      .then(() => {
        this.setState({
          showArchiveConfirmation: false,
          showMarkDeadConfirmation: false,
          project: null
        })
        this._refresh()
      })
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  render() {
    const {
      project,
      showArchiveConfirmation,
      showMarkDeadConfirmation,
      error,
      redirect,
      refresh
    } = this.state

    const activeStates = [
      'request',
      'quoted',
      'awarded',
      'production',
      'review',
      'delivery'
    ]

    // Just skip everything if we're redirecting
    if (redirect) return <Redirect push to={redirect} />

    return (
      <section>
        {refresh && <LinearLoader />}
        {!refresh && (
          <ListProjects
            serverFilters={{ states: activeStates }}
            rowDetailComponent={({ row }) => (
              <ProjectDetailsRow
                row={row}
                archiveCallback={() => this._confirmArchive(row)}
                markDeadCallback={() => this._confirmMarkDead(row)}
              />
            )}
          />
        )}

        {project && (
          <ProjectArchiveConfirmation
            project={project}
            open={showArchiveConfirmation}
            archiveCallback={() => this._updateProjectState('archived')}
            cancelCallback={this._cancelArchive}
          />
        )}

        {project && (
          <ProjectMarkDeadConfirmation
            project={project}
            open={showMarkDeadConfirmation}
            markDeadCallback={() => this._updateProjectState('dead')}
            cancelCallback={this._cancelMarkDead}
          />
        )}

        <ErrorDialog
          open={Boolean(error)}
          closeCallback={() => this.setState({ error: null })}
        >
          <p>
            <strong>Error:</strong> {error}
          </p>
        </ErrorDialog>
      </section>
    )
  }
}

export default ProjectsManager



// WEBPACK FOOTER //
// ./src/components/Projects/ProjectsManager.jsx